import { Card, Row, Tag } from 'antd';
import React, { FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { ENV } from 'src/';
import { useCheckGlobalLicensesPermission } from 'src/common/hooks/use-check-global-licenses-permission';
import { emptyTableModel } from 'src/common/utils';
import { AppConfiguration } from 'src/core/services/configuration.service';
import { formatMessage } from 'src/core/services/http.service';
import { Query } from 'src/core/stores/data-store';
import { TableModel, TableView } from 'src/core/ui/collections/table';
import { setHeader, setRoutes } from 'src/core/ui/shell';
import { AppConfig, container } from 'src/inversify.config';
import { GroupItemStore, GroupSummary, GroupSummaryStore, GroupType } from 'src/stores/groups-store';
import NewGroupDialog from './group-new';

const LicenseManagerGroupListPage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const config: AppConfiguration = useContext(AppConfig);
  const [query, setQuery] = useState({
    searchQuery: '',
    orderBy: [{ field: 'name', direction: 'Ascending', useProfile: false }],
    skip: 0,
    take: 10,
    filter: { groupType: GroupType.License },
  } as Query);

  const groupsStore = container.get(GroupSummaryStore);
  const groupFormStore = container.get(GroupItemStore);
  const groupsState = groupsStore.state;
  const groupFormState = groupFormStore.state;
  const [hasManageGlobalLicensesPermission] = useCheckGlobalLicensesPermission();

  const [newGroupVisible, setNewGroupVisible] = useState(false);

  setHeader(t('Groups'));
  setRoutes([
    { path: '', breadcrumbName: t('Home') },
    { path: 'groups', breadcrumbName: t('Groups') },
  ]);

  useEffect(
    () => {
      groupsStore.loadWithTypes(query, [GroupType.License]);
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const tableModel = {
    query,
    columns: [
      {
        sortable: true,
        searchable: true,
        field: 'name',
        title: t('Name'),
        renderer: (data) => (
          <span>
            <Link to={`${data.id}`}>
              <span>{data.name}</span>
            </Link>
          </span>
        ),
      },
      {
        field: 'organizationId',
        title: t('Organization'),
        renderer: (data: GroupSummary) => (
          <span>
            <Tag>{data.organization}</Tag>
          </span>
        ),
      },
    ],
    data: { ...groupsState.value, isBusy: groupsState.value.isBusy || groupFormState.value.isBusy },
  } as TableModel<GroupSummary>;

  return (
    <Row align="top" justify="space-between">
      <Card className="card-table" style={{ width: '100%' }}>
        <TableView
          rowKey="id"
          canCreateNew
          onNewItem={hasManageGlobalLicensesPermission ? () => setNewGroupVisible(true) : undefined}
          newItemName={t('AddGroup')}
          canDelete
          onDeleteRow={(item: any) => groupsStore.delete(item.id)}
          canEdit={false}
          canSort
          onQueryChanged={(query: Query) => {
            setQuery(query);
            groupsStore.loadWithTypes(query, [GroupType.License]);
          }}
          onRefresh={() => groupsStore.loadWithTypes(query, [GroupType.License])}
          model={groupsState.errorMessage.value ? emptyTableModel : tableModel}
          error={
            groupsState.errorMessage.value && (config.environment === ENV.DEV ? formatMessage(groupsState.errorMessage.value) : t('GroupsTableError'))
          }
        />
        {newGroupVisible && (
          <NewGroupDialog
            groupType={GroupType.License}
            onClose={(response) => {
              setNewGroupVisible(false);
              if (response) {
                navigate(`${response.identity}`);
              }
            }}
          />
        )}
      </Card>
    </Row>
  );
};

export default LicenseManagerGroupListPage;
